//@flow
import {IconButton, makeStyles} from '@material-ui/core';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {MESSAGE_FORM_FIELDS} from '../../../constants';
import {Add, CloseOutlined} from '@material-ui/icons';
import FileTypeIcons from '../../../../../../../../elements/FileTypeIcons';
import {colors} from '../../../../../../../../assets/styles/colors';
import ImageRenderer from '../../../../../../../../elements/ImageRenderer';
import CustomHorizontalScroll from '../../../../../../../../elements/CustomHorizontalScroll';

import type {MessageFormAttachment} from '../../../constants';

const useStyles = makeStyles(() => ({
  attachmentFiles: {
    display: 'flex',
    gap: '.5rem',
    padding: '.5rem',
    background: 'white',
    borderRadius: '.5rem .5rem 0 0',
    overflowX: 'auto',
    width: '100%',
    boxShadow: 'rgba(17, 12, 46, 0.1) 0px -24px 32px 0px',
  },
  attachmentPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    background: colors.grey200,
    fontSize: '1.25rem',
    flexDirection: 'column',
    gap: '.25rem',

    '&[data-document]': {
      minWidth: '6rem',
      paddingTop: '.25rem',
      width: '6rem',
    },

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  attachmentPreviewName: {
    fontSize: '.75rem',
    textAlign: 'center',
    width: '95%',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  attachmentPreviewRemove: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: '.25rem',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    fontSize: '.5rem',
  },
  addAttachment: {
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    padding: '0',
    borderRadius: '.5rem',
    background: colors.grey200,

    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export type AttachmentPreviewProps = {
  showAddButton?: boolean,
};

export default function AttachmentPreview(props: AttachmentPreviewProps) {
  const {showAddButton} = props;

  const {watch, setValue} = useFormContext();

  const classes = useStyles();

  const files = watch(MESSAGE_FORM_FIELDS.files, []);
  const attachment: MessageFormAttachment | typeof undefined = watch(
    MESSAGE_FORM_FIELDS.attachment
  );

  if (!files.length && !attachment) return null;

  const handleRemoveFile = (file: File) => {
    const newFiles = files.filter(f => f.name !== file.name);
    setValue(MESSAGE_FORM_FIELDS.files, newFiles);
  };

  const handleAddAttachment = (uploadedFiles: File[]) => {
    const uniqueUploadedFiles = uploadedFiles.filter(
      file => !files.find(f => f.name === file.name)
    );

    const newFiles = [...files, ...uniqueUploadedFiles];

    setValue(MESSAGE_FORM_FIELDS.files, newFiles);
  };

  return (
    <CustomHorizontalScroll
      classes={{
        scrollContainer: classes.attachmentFiles,
      }}
    >
      {files.map(file => (
        <AttachmentItem
          key={file.name}
          type={file.type}
          name={file.name}
          src={file}
          onRemove={() => {
            handleRemoveFile(file);
          }}
        />
      ))}
      {attachment && (
        <AttachmentItem
          key={attachment.name}
          type={attachment.type}
          name={attachment.name || 'Archivo adjunto'}
          src={attachment.src}
          onRemove={() => {
            setValue(MESSAGE_FORM_FIELDS.attachment, undefined);
          }}
        />
      )}
      {showAddButton && (
        <label className={classes.addAttachment}>
          <Add />
          <input
            type="file"
            hidden
            multiple
            onInput={e => {
              handleAddAttachment(Array.from(e.target.files));
              e.target.value = null;
            }}
          />
        </label>
      )}
    </CustomHorizontalScroll>
  );
}

type AttachmentItemProps = {
  onRemove: () => void,
  type: string,
  name: string,
  src: string | File | Blob,
};
function AttachmentItem({type, name, src, onRemove}: AttachmentItemProps) {
  const classes = useStyles();

  const isImage = type.includes('image');

  if (isImage) {
    return (
      <picture className={classes.attachmentPreview}>
        <IconButton
          className={classes.attachmentPreviewRemove}
          size="small"
          onClick={onRemove}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>

        <ImageRenderer src={src} alt={name} />
      </picture>
    );
  }

  return (
    <div className={classes.attachmentPreview} data-document>
      <IconButton
        className={classes.attachmentPreviewRemove}
        size="small"
        onClick={onRemove}
      >
        <CloseOutlined fontSize="small" />
      </IconButton>

      <FileTypeIcons type={type} />

      <span className={classes.attachmentPreviewName}>{name}</span>
    </div>
  );
}
